import type { InvoiceItem } from "@/lib/api/types";

/**
 * Where a voided or refunded plate's cooked food went (phase 12, PO-9, D141):
 *
 * - `RETURNED` — the cashier answered «مرتجع من المطبخ» and the server wrote a
 *   RETURNED_TO_STOCK row (`InvoiceItem.returnedToStock`, derived off the
 *   depletion ledger on every staff read);
 * - `WASTED` — the plate was voided or refunded AFTER the kitchen cooked it
 *   (it reached PREPARING — D20's "was it cooked", `preparingAt`) and nothing
 *   went back: «هالك», the default;
 * - `null` — not voided or refunded, or voided before anything was cooked (a
 *   QUEUED plate struck off: there is no food to speak of).
 *
 * The till and the back-office bill detail both read it, so an owner checking
 * a cashier's void sees the same answer the item card shows.
 */
export type PlateFoodFate = "RETURNED" | "WASTED";

export function plateFoodFate(
  item: Pick<
    InvoiceItem,
    "fulfillmentState" | "refunded" | "preparingAt" | "returnedToStock"
  >,
): PlateFoodFate | null {
  if (item.returnedToStock) return "RETURNED";
  const struck = item.fulfillmentState === "VOIDED" || item.refunded;
  if (!struck) return null;
  return item.preparingAt !== null ? "WASTED" : null;
}
